import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Plus, RefreshCw, XCircle } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import RequireAdmin from "@/components/admin/RequireAdmin";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { useAdminI18n } from "@/lib/adminI18n";

const APPROVAL_FIELD_LABELS = {
  status: { en: "Status", ar: "الحالة" },
  role: { en: "Role", ar: "الدور" },
  email: { en: "Email", ar: "البريد الإلكتروني" },
  name: { en: "Name", ar: "الاسم" },
  hidden: { en: "Hidden", ar: "مخفي" },
  enabled: { en: "Enabled", ar: "مفعّل" },
  sort_order: { en: "Sort order", ar: "ترتيب الظهور" },
};

function pickLabel(map, key, lang) {
  const entry = map[String(key || "")];
  if (entry) return entry[lang] || entry.en;
  return String(key || "")
    .replace(/[_.-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatApprovalValue(value, lang) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? (lang === "ar" ? "نعم" : "Yes") : (lang === "ar" ? "لا" : "No");
  if (typeof value === "number") return String(value);
  if (Array.isArray(value)) return value.map((item) => formatApprovalValue(item, lang)).join(", ");
  if (typeof value === "object") return value.name || value.title || value.label || value.email || value.id || "—";
  return String(value);
}

function approvalRows(value, lang) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return [];
  return Object.entries(value)
    .filter(([key]) => !["password", "password_hash", "token", "_id"].includes(String(key).toLowerCase()))
    .slice(0, 8)
    .map(([key, val]) => ({ label: pickLabel(APPROVAL_FIELD_LABELS, key, lang), value: formatApprovalValue(val, lang) }));
}

function AdminApprovalsPageInner() {
  const { apiFetch } = useAdminAuth();
  const { t, lang } = useAdminI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", description: "", item_kind: "settings", item_id: "" });

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiFetch("/admin/approval-requests");
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || t("approvals.loadFailed", "Could not load approvals"));
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      toast.error(err?.message || t("approvals.loadFailed", "Could not load approvals"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async (e) => {
    e.preventDefault();
    try {
      const res = await apiFetch("/admin/approval-requests", {
        method: "POST",
        body: JSON.stringify({ ...form, item_id: form.item_id || undefined, payload: {} }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || t("approvals.createFailed", "Create failed"));
      setForm({ title: "", description: "", item_kind: "settings", item_id: "" });
      toast.success(t("approvals.created", "Approval request created"));
      load();
    } catch (err) {
      toast.error(err?.message || t("approvals.createFailed", "Create failed"));
    }
  };

  const decide = async (item, decision) => {
    const note = window.prompt(t("approvals.decisionNotePrompt", "Optional decision note"), "");
    if (note === null) return;
    try {
      const res = await apiFetch(`/admin/approval-requests/${encodeURIComponent(item.id)}/${decision}`, {
        method: "POST",
        body: JSON.stringify({ note }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || t("approvals.decisionFailed", "Decision failed"));
      setItems((rows) => rows.map((row) => (row.id === item.id ? data.item : row)));
      toast.success(decision === "approve" ? t("approvals.approved", "Approval approved") : t("approvals.rejected", "Approval rejected"));
    } catch (err) {
      toast.error(err?.message || t("approvals.decisionFailed", "Decision failed"));
    }
  };

  return (
    <AdminShell
      title={t("approvals.title", "Approvals")}
      description={t("approvals.description", "Review controlled admin changes before they are treated as approved operational requests.")}
      actions={(
        <button type="button" onClick={load} className="admin-shell-action">
          <RefreshCw className="h-4 w-4" />
          <span>{t("dashboard.refresh", "Refresh")}</span>
        </button>
      )}
    >
      <div className="grid gap-6 xl:grid-cols-[0.9fr_1.7fr]">
        <form onSubmit={create} className="rounded-3xl border border-white/10 bg-white/[0.045] p-5">
          <div className="mb-4 flex items-center gap-2">
            <Plus className="h-5 w-5 text-[#F58D4A]" />
            <h2 className="text-lg font-semibold">{t("approvals.newRequest", "New request")}</h2>
          </div>
          <div className="space-y-3">
            <TextField label={t("approvals.requestTitle", "Title")} value={form.title} onChange={(title) => setForm((f) => ({ ...f, title }))} />
            <TextField label={t("approvals.area", "Area")} value={form.item_kind} onChange={(item_kind) => setForm((f) => ({ ...f, item_kind }))} />
            <TextField label={t("approvals.targetId", "Target ID")} value={form.item_id} onChange={(item_id) => setForm((f) => ({ ...f, item_id }))} />
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/55">{t("approvals.requestDescription", "Description")}</span>
              <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={4} className="mt-1.5 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-[#F37021] focus:outline-none" />
            </label>
            <button disabled={!form.title} className="h-11 w-full rounded-full bg-[#F37021] font-semibold text-white disabled:opacity-50">{t("approvals.createRequest", "Create request")}</button>
          </div>
        </form>

        <section className="rounded-3xl border border-white/10 bg-white/[0.045] p-5">
          <h2 className="mb-4 text-lg font-semibold">{t("approvals.requests", "Requests")}</h2>
          {loading ? (
            <div className="py-8 text-sm text-white/50">{t("approvals.loading", "Loading approvals...")}</div>
          ) : items.length === 0 ? (
            <div className="py-8 text-sm text-white/50">{t("approvals.empty", "No approval requests yet.")}</div>
          ) : (
            <div className="space-y-3">
              {items.map((item) => {
                const beforeRows = approvalRows(item.payload?.before, lang);
                const afterRows = approvalRows(item.payload?.after, lang);
                const requester = item.requested_by_name || item.requested_by || "—";
                return (
                  <div key={item.id} className="rounded-2xl border border-white/10 bg-black/20 p-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="font-semibold text-white">{item.title}</h3>
                          <span className="rounded-full border border-white/10 px-2 py-1 text-[11px] uppercase tracking-[0.16em] text-white/55">{item.status}</span>
                        </div>
                        <div className="mt-1 text-xs text-white/45">{item.item_kind}{item.item_id ? ` - ${item.item_id}` : ""} - {item.created_at?.slice?.(0, 19)}</div>
                        <div className="mt-2 grid gap-1 text-xs text-white/50 sm:grid-cols-2">
                          <div>{t("approvals.requestedBy", "Requested by")}: {requester}{item.requested_by_role ? ` (${item.requested_by_role})` : ""}</div>
                          {item.reviewed_by && <div>{t("approvals.reviewedBy", "Reviewed by")}: {item.reviewed_by}</div>}
                          {item.reviewed_at && <div>{t("approvals.reviewedAt", "Reviewed at")}: {item.reviewed_at.slice(0, 19)}</div>}
                          {item.decision_note && <div>{t("approvals.note", "Note")}: {item.decision_note}</div>}
                        </div>
                        {item.description && <p className="mt-3 text-sm leading-6 text-white/62">{item.description}</p>}
                        {(beforeRows.length > 0 || afterRows.length > 0) && (
                          <div className="mt-3 grid gap-2 text-xs md:grid-cols-2">
                            {beforeRows.length > 0 && <ChangePanel title={t("dashboard.before", "Before")} rows={beforeRows} tone="red" />}
                            {afterRows.length > 0 && <ChangePanel title={t("dashboard.after", "After")} rows={afterRows} tone="emerald" />}
                          </div>
                        )}
                      </div>
                      {item.status === "pending" && (
                        <div className="flex shrink-0 gap-2">
                          <button type="button" onClick={() => decide(item, "approve")} className="inline-flex h-10 items-center gap-2 rounded-full bg-emerald-500 px-4 text-sm font-semibold text-white">
                            <CheckCircle2 className="h-4 w-4" />
                            {t("approvals.approve", "Approve")}
                          </button>
                          <button type="button" onClick={() => decide(item, "reject")} className="inline-flex h-10 items-center gap-2 rounded-full bg-red-500 px-4 text-sm font-semibold text-white">
                            <XCircle className="h-4 w-4" />
                            {t("approvals.reject", "Reject")}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </AdminShell>
  );
}

function ChangePanel({ title, rows, tone }) {
  const toneClass = tone === "red"
    ? "border-red-500/20 bg-red-500/5 text-red-100"
    : "border-emerald-500/20 bg-emerald-500/5 text-emerald-100";
  return (
    <div className={`rounded-xl border p-3 ${toneClass}`}>
      <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] opacity-65">{title}</div>
      <div className="space-y-1">
        {rows.map((row) => (
          <div key={row.label} className="flex justify-between gap-3">
            <span className="opacity-60">{row.label}</span>
            <span className="text-right">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TextField({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/55">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} className="mt-1.5 h-10 w-full rounded-xl border border-white/10 bg-black/40 px-3 text-sm text-white focus:border-[#F37021] focus:outline-none" />
    </label>
  );
}

export default function AdminApprovalsPage() {
  return (
    <RequireAdmin permission="approvals.manage">
      <AdminApprovalsPageInner />
    </RequireAdmin>
  );
}
